/**
 * Dispatch Intelligence Engine
 *
 * Derives a prioritized, action-driven queue from all active jobs.
 * Every job gets a deterministic nextAction — zero dead jobs.
 *
 * ADDITIVE — consumes existing state, does not mutate.
 */

import type { Job, Issue } from "./types";
import type { InstallerWrapRecord } from "./installerWrapEngine";
import type { InstallFollowUpTask } from "./unifiedFollowUp";
import { deriveJobState, deriveSchedulingClearance, checkFinancialIntegrity, type DerivedJobState } from "./jobStateDerived";
import { getStageConfig } from "./workflowEngine";
import { hasWrapForToday } from "./installerWrapEngine";

// ── Types ──

export type DispatchPriority = "critical" | "high" | "medium" | "low";

export type DispatchFilter =
  | "all"
  | "blocked"
  | "ready_to_schedule"
  | "in_progress"
  | "payment_required"
  | "qa_ready"
  | "stalled";

export interface DispatchQueueItem {
  jobId: string;
  jobNumber: string;
  jobDisplayRef: string;
  customerName: string;
  currentStage: string;
  projectType: string;
  address: string;
  state: DerivedJobState;
  nextAction: string;
  nextActionCta: string;
  nextActionRoute: string;
  priority: DispatchPriority;
  blockerReason: string | null;
  paymentLabel: string;
  installLabel: string;
  staleDays: number;
  flags: string[];
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

// ── Next Action Engine ──

function deriveNextAction(
  job: Job,
  state: DerivedJobState,
  wrapRecords: InstallerWrapRecord[],
  followUpTasks: InstallFollowUpTask[],
): { action: string; cta: string; route: string } {
  const order = getStageConfig(job.currentStage)?.order ?? 0;

  // Completed
  if (state.install === "completed") {
    return { action: "Completed", cta: "View Job", route: `/jobs/${job.id}` };
  }

  // QA Review
  if (state.install === "qa_review") {
    if (state.blockers.length > 0) {
      return { action: "Resolve Blockers for QA", cta: "Review QA", route: `/closeout` };
    }
    return { action: "Approve Closeout", cta: "Review QA", route: `/closeout` };
  }

  // Install complete but not in QA yet
  if (state.install === "install_complete") {
    if (state.payment === "payment_pending" || state.payment === "payment_deferred") {
      return { action: "Resolve Payment", cta: "Open Job", route: `/jobs/${job.id}` };
    }
    return { action: "Submit for QA", cta: "Open Job", route: `/jobs/${job.id}` };
  }

  // In progress
  if (state.install === "in_progress" || state.install === "partial_continuation") {
    const installerId = job.assignedCrew || "installer";
    const needsWrap = !hasWrapForToday(job.id, installerId, wrapRecords);
    if (needsWrap && order >= 80) {
      return { action: "Submit Wrap", cta: "Open Installer", route: `/installer-dashboard` };
    }
    if (state.install === "partial_continuation") {
      return { action: "Continue Install", cta: "Open Installer", route: `/installer-dashboard` };
    }
    return { action: "Complete Install", cta: "Open Installer", route: `/installer-dashboard` };
  }

  // Blocked
  if (state.install === "blocked") {
    return { action: "Resolve Issue", cta: "View Issues", route: `/jobs/${job.id}` };
  }

  // Not started — pre-install stages
  if (state.payment === "deposit_pending") {
    return { action: "Collect Deposit", cta: "Open Job", route: `/jobs/${job.id}` };
  }

  // Scheduling clearance
  const schedClearance = deriveSchedulingClearance(job);
  if (order < 70) {
    // Pre-scheduling
    if (job.materialReadiness === "Not Ordered" || job.materialReadiness === "Missing Items") {
      return { action: "Order Materials", cta: "Open Job", route: `/jobs/${job.id}` };
    }
    if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") {
      return { action: "Obtain Permit", cta: "Permitting", route: `/permitting` };
    }
    if (!schedClearance.allowed) {
      return { action: "Clear for Scheduling", cta: "Open Job", route: `/jobs/${job.id}` };
    }
    return { action: "Schedule Install", cta: "Schedule", route: `/scheduling` };
  }

  // Fallback for scheduled/ready
  if (order === 70) {
    return { action: "Dispatch Crew", cta: "Dispatch", route: `/scheduling-dispatch` };
  }

  return { action: "Review Job", cta: "Open Job", route: `/jobs/${job.id}` };
}

// ── Priority Derivation ──

function derivePriority(
  job: Job,
  state: DerivedJobState,
  wrapRecords: InstallerWrapRecord[],
  staleDays: number,
): DispatchPriority {
  // Critical: in-progress without today's wrap
  const order = getStageConfig(job.currentStage)?.order ?? 0;
  if (order >= 80 && order < 90) {
    const installerId = job.assignedCrew || "installer";
    if (!hasWrapForToday(job.id, installerId, wrapRecords)) return "critical";
  }

  // Critical: payment unresolved post-install
  if (state.install === "install_complete" && (state.payment === "payment_pending" || state.payment === "payment_deferred")) {
    return "critical";
  }

  // High: blocked or has open issues
  if (state.install === "blocked") return "high";
  if (state.blockers.length > 0) return "high";
  if (staleDays > 7) return "high";

  // Medium: ready for action
  if (state.install === "qa_review") return "medium";
  if (state.install === "not_started" && state.payment !== "deposit_pending") return "medium";

  // Low: completed or early stages
  if (state.install === "completed") return "low";

  return "medium";
}

// ── Stale Detection ──

function computeStaleDays(job: Job): number {
  const lastActivity = job.activityHistory?.[job.activityHistory.length - 1];
  if (!lastActivity?.date) return 0;
  const lastDate = new Date(lastActivity.date);
  const now = new Date();
  return Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
}

// ── Flags ──

function deriveFlags(
  job: Job,
  state: DerivedJobState,
  staleDays: number,
  integrity: { valid: boolean },
): string[] {
  const flags: string[] = [];
  if (staleDays > 7) flags.push("Stalled");
  if (!integrity.valid) flags.push("Financial Discrepancy");
  if (state.payment === "payment_deferred") flags.push("Deferred Payment");
  if (state.payment === "financial_hold") flags.push("Financial Hold");
  if (state.followUp === "follow_up_required") flags.push("Follow-Up Required");
  return flags;
}

// ── Payment Label ──

const PAYMENT_LABELS: Record<string, string> = {
  no_payment_required: "No Payment",
  deposit_pending: "Deposit Due",
  deposit_received: "Deposit Paid",
  payment_collected: "Collected",
  payment_pending: "Payment Due",
  payment_deferred: "Deferred",
  financially_cleared: "Cleared",
  financial_hold: "Hold",
};

// ── Install Label ──

const INSTALL_LABELS: Record<string, string> = {
  not_started: "Pre-Install",
  in_progress: "In Progress",
  partial_continuation: "Continuation",
  blocked: "Blocked",
  install_complete: "Complete",
  qa_review: "QA Review",
  completed: "Closed",
};

// ── Blocker Derivation ──

function deriveBlocker(state: DerivedJobState, job: Job): string | null {
  if (state.blockers.length > 0) return state.blockers[0];

  if (state.payment === "deposit_pending") return "Deposit not paid";
  if (state.payment === "financial_hold") return "Financial hold — issue pending";

  if (job.materialReadiness === "Not Ordered") return "Materials not ordered";
  if (job.materialReadiness === "Missing Items") return "Materials missing";
  if (job.permitRequired && job.permitStatus === "Submitted") return "Permit pending approval";
  if (job.permitRequired && (job.permitStatus as string) === "Rejected") return "Permit rejected";

  return null;
}

// ── Main Queue Builder ──

export function buildDispatchQueue(
  jobs: Job[],
  issues: Issue[],
  wrapRecords: InstallerWrapRecord[],
  followUpTasks: InstallFollowUpTask[],
): DispatchQueueItem[] {
  return jobs
    .filter(j => j.currentStage !== "Completed")
    .map(job => {
      const state = deriveJobState(job, issues, wrapRecords);
      const staleDays = computeStaleDays(job);
      const integrity = checkFinancialIntegrity(job);
      const { action, cta, route } = deriveNextAction(job, state, wrapRecords, followUpTasks);
      const priority = derivePriority(job, state, wrapRecords, staleDays);
      const blocker = deriveBlocker(state, job);
      const flags = deriveFlags(job, state, staleDays, integrity);

      const address = typeof job.address === "string"
        ? job.address
        : `${job.address.street}, ${job.address.city}`;

      const displayRef = String(job.jobNumber || "").trim();
      const jobDisplayRef = displayRef && !isUuidLike(displayRef)
        ? displayRef
        : (isUuidLike(job.id) ? "JOB-UNASSIGNED" : job.id);

      return {
         jobId: job.id,
         jobNumber: job.jobNumber || job.id,
         jobDisplayRef,
         customerName: job.customerName,
         currentStage: job.currentStage,
         projectType: job.projectType,
         address,
         state,
         nextAction: action,
         nextActionCta: cta,
         nextActionRoute: route,
         priority,
         blockerReason: blocker,
         paymentLabel: PAYMENT_LABELS[state.payment] ?? state.payment,
         installLabel: INSTALL_LABELS[state.install] ?? state.install,
         staleDays,
         flags,
       };
    })
    .sort((a, b) => {
      const pOrder: Record<DispatchPriority, number> = { critical: 0, high: 1, medium: 2, low: 3 };
      return pOrder[a.priority] - pOrder[b.priority];
    });
}

// ── Filter Logic ──

export function filterDispatchQueue(
  queue: DispatchQueueItem[],
  filter: DispatchFilter,
): DispatchQueueItem[] {
  switch (filter) {
    case "blocked":
      return queue.filter(q => q.blockerReason !== null);
    case "ready_to_schedule":
      return queue.filter(q => q.nextAction === "Schedule Install" || q.nextAction === "Dispatch Crew");
    case "in_progress":
      return queue.filter(q => q.state.install === "in_progress" || q.state.install === "partial_continuation");
    case "payment_required":
      return queue.filter(q =>
        q.state.payment === "deposit_pending" ||
        q.state.payment === "payment_pending" ||
        q.state.payment === "payment_deferred"
      );
    case "qa_ready":
      return queue.filter(q => q.state.install === "qa_review");
    case "stalled":
      return queue.filter(q => q.flags.includes("Stalled"));
    default:
      return queue;
  }
}
